import React, { useState, useEffect } from 'react';
import { Jumbotron, Container, Row } from 'react-bootstrap';
import { camelizeKeys } from 'humps';
import axios from 'axios';
import NP from 'number-precision';

import SaveButton from './SaveButton';
import Finances from './Finances';
import ExpressCargoFields from './ExpressCargoFields';
import ItemInput from './ItemInput';
import ItemList from './ItemList';
import GeneralCargoFields from './GeneralCargoFields';

/* Computes the Dimension
 * (*L * H *W ) / 3500.00
 */
const dimensionDivider = 3500.0;

const AddShipmentItems = (props) => {
  const multiplyFreightCharge = props.multiplyFreightCharge;
  const calculator = camelizeKeys(props.calculator);
  const currentItems = camelizeKeys(props.items);
  const discount = props.discount ? camelizeKeys(props.discount) : null;
  const { id } = props;
  const DEFAULT_PARAMS = {
    declaredValue: 0,
    name: '',
    uom: 'pcs',
    qty: 1,
    freightCharge: NP.divide(calculator.minimumWeightChargeCents, 100),
    actualWeight: 0,
    dimensionLength: 0,
    dimensionWidth: 0,
    dimensionHeight: 0,
    dimension: 0,
  };

  /* If minimum weight is preset, it's a cargo calculator type */
  const isFlatFee = calculator.minimumWeight === null;

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [items, setItems] = useState(
    currentItems.map((item) => ({
      ...item,
      amount: NP.divide(item.amount, 100),
    }))
  );

  /* On Express Cargo using the Flat Fee, it can only have 1 item */
  const expressCanAddItem = isFlatFee && items.length === 1;

  const toPercent = (num) => NP.times(num, 0.01);
  const tax = NP.times(params.freightCharge, toPercent(calculator.taxPercent));

  const valuation = NP.times(
    params.declaredValue,
    toPercent(calculator.valuationPercent)
  );

  const total = items.reduce((total, item) => NP.plus(item.amount, total), 0);
  const discountPercent = discount
    ? toPercent(NP.divide(discount.amountCents, 100))
    : 0;
  const grandTotal = NP.round(
    NP.minus(total, NP.times(total, discountPercent)),
    2
  );

  /* Compuetes the dimension in params */
  useEffect(() => {
    const computeDimension = () => {
      const { dimensionHeight, dimensionWidth, dimensionLength } = params;
      const dimension = NP.divide(
        NP.times(dimensionHeight, dimensionLength, dimensionWidth),
        dimensionDivider
      );
      setParams((prev) => ({
        ...prev,
        dimension,
      }));
    };

    /* only for general cargo */
    !isFlatFee && computeDimension();
  }, [
    params.dimensionHeight,
    params.dimensionWidth,
    params.dimensionLength,
    setParams,
  ]);

  /* Computes the freight charge for general cargo*/
  useEffect(() => {
    const computeFreightCharge = () => {
      const actualWeight = NP.strip(params.actualWeight);
      const dimension = NP.strip(params.dimension);
      const weight = actualWeight > dimension ? actualWeight : dimension;
      const exceedingWeight =
        weight > NP.strip(calculator.minimumWeight)
          ? NP.minus(weight, calculator.minimumWeight)
          : 0;
      const minimumCharge = NP.divide(calculator.minimumWeightChargeCents, 100);
      const excessCharge = NP.times(
        exceedingWeight,
        NP.divide(calculator.chargePerUnitCents, 100)
      );
      let freightCharge = NP.plus(minimumCharge, excessCharge);
      if (multiplyFreightCharge) {
        freightCharge = NP.times(freightCharge, params.qty);
      }

      /* Then add the valuation */
      freightCharge = NP.plus(freightCharge, valuation);

      setParams((prev) => ({
        ...prev,
        freightCharge,
        data: {
          ...prev.data,
          weight,
          exceedingWeight,
          minimumCharge,
          excessCharge,
          dimensionHeight: prev.dimensionHeight,
          dimensionLength: prev.dimensionLength,
          dimensionWidth: prev.dimensionWidth,
        },
      }));
    };

    /* only for general cargo */
    !isFlatFee && computeFreightCharge();
  }, [
    setParams,
    params.actualWeight,
    params.dimension,
    params.declaredValue,
    params.qty,
  ]);

  /* Computes the freight charge for express cargo*/
  useEffect(() => {
    const computeFreightCharge = () => {
      const flatFee = NP.divide(calculator.flatFeeCents, 100);
      const freightCharge = NP.plus(flatFee, valuation);
      setParams((prev) => ({
        ...prev,
        freightCharge,
      }));
    };

    /* only for exress cargo */
    isFlatFee && computeFreightCharge();
  }, [setParams, params.declaredValue, params.qty]);

  const addItem = () => {
    setErrors({});

    if ([null, undefined, ''].includes(params.declaredValue)) {
      return setErrors({ declaredValue: "Can't be blank" });
    }
    if (params.declaredValue < 0) {
      return setErrors({ declaredValue: "Can't be negative" });
    }
    if (!params.name) {
      return setErrors({ name: "Can't be blank" });
    }

    const itemFreightCharge = NP.plus(params.freightCharge, tax);
    setItems([...items, { ...params, amount: itemFreightCharge }]);

    setParams(DEFAULT_PARAMS);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setParams({ ...params, [name]: value });
  };

  const removeItem = (item) => {
    const newItems = items.filter((i) => i !== item);
    setItems(newItems);
  };

  const save = () => {
    setIsSaving(true);
    axios({
      method: 'PUT',
      url: `/shipments/${id}`,
      data: { items: items, id },
    })
      .then((res) => {
        window.location.replace(
          `/admin/${isFlatFee ? 'express_cargos' : 'general_cargos'}/${
            res.data.shipment.id
          }`
        );
      })
      .catch((err) => {
        console.log(err);
        setIsSaving(false);
      });
  };

  return (
    <Container fluid>
      <Jumbotron>
        {items.length > 0 && <SaveButton save={save} isSaving={isSaving} />}
        <hr />
        <Finances grandTotal={grandTotal} total={total} discount={discount} />
        <hr />
        {isFlatFee ? (
          <ExpressCargoFields
            params={params}
            errors={errors}
            onChange={onChange}
            calculator={calculator}
            tax={tax}
            valuation={valuation}
          />
        ) : (
          <GeneralCargoFields
            params={params}
            errors={errors}
            onChange={onChange}
            calculator={calculator}
            tax={tax}
            valuation={valuation}
          />
        )}
        <hr />
        <ItemInput
          params={params}
          errors={errors}
          onChange={onChange}
          canAddItem={expressCanAddItem}
          addItem={addItem}
        />
        <ItemList items={items} removeItem={removeItem} />
      </Jumbotron>
    </Container>
  );
};

export default AddShipmentItems;
